import { action, observable, makeObservable } from 'mobx'
import SilentListStore from 'stores/SilentListStore'
import ClientsService from 'services/ClientsService'
import Paginator from 'models/Paginator'
import Sorter from 'models/Sorter'

class ClientsStore extends SilentListStore {
    DEFAULT_SORT_FIELD = 'displayName'
    DEFAULT_SORT_SENSE = 'ASC'
    clients = []
    currentPage = 1
    filterText = ''
    paginator = Paginator.empty()
    sorter = Sorter.fromJson({
        sortField: this.DEFAULT_SORT_FIELD,
        sortSense: this.DEFAULT_SORT_SENSE,
    })

    clientTypeSelected = null
    filterSelectedBroker = null
    filterSelectedOrigin = null

    constructor() {
        super({ itemsKey: 'clients' })

        makeObservable(this, {
            // observables
            clients: observable,
            currentPage: observable,
            filterText: observable,
            paginator: observable,
            sorter: observable,
            clientTypeSelected: observable,
            filterSelectedBroker: observable,
            filterSelectedOrigin: observable,
            setDefaultSort: observable,
            // actions
            changePage: action,
            changeSort: action,
            searchClient: action,
            listClients: action,
            filterCustomersByType: action,
            filterCustomersByBroker: action,
            filterCustomersByOrigin: action,
            ...this.getSilentListAnnotations(),
        })

        this.clientsService = new ClientsService()
    }

    getListCacheKey() {
        return [
            this.clientTypeSelected || '',
            this.filterText || '',
            this.filterSelectedBroker || '',
            this.filterSelectedOrigin || '',
            this.currentPage,
            this.sorter.sortField,
            this.sorter.sortSense,
        ].join('|')
    }

    setDefaultSort() {
        this.setSort(this.DEFAULT_SORT_FIELD, this.DEFAULT_SORT_SENSE)
    }

    changePage(page) {
        this.currentPage = page
    }

    changeSort(sortField) {
        this.sorter.changeSort(sortField)
    }

    setSort(sortField, sortSense) {
        if (sortField && sortSense) {
            this.sorter.setSort(sortField, sortSense)
        } else {
            this.setDefaultSort()
        }
    }

    searchClient(searchText) {
        this.filterText = searchText
    }

    async listClients({ silent = false } = {}) {
        await this.fetchList({
            silent,
            onRetry: () => this.listClients(),
            request: async () => {
                let { sortField } = this.sorter

                if (sortField === 'displayName') {
                    sortField = 'name'
                }

                const params = {
                    page: this.currentPage,
                    name: this.filterText,
                    [`sort[${sortField}]`]: this.sorter.sortSense,
                    type: this.clientTypeSelected,
                    broker: this.filterSelectedBroker,
                    group: this.filterSelectedOrigin ? this.filterSelectedOrigin : null,
                }
                const response = await this.clientsService.getClients(params)

                return {
                    items: response.clients,
                    paginator: response.paginator,
                }
            },
        })
    }

    filterCustomersByType(clientType) {
        if (clientType) {
            this.clientTypeSelected = clientType
        } else {
            this.clientTypeSelected = ''
        }
    }

    filterCustomersByBroker(broker) {
        if (broker) {
            this.filterSelectedBroker = broker
        } else {
            this.filterSelectedBroker = ''
        }
    }

    filterCustomersByOrigin(group) {
        this.filterSelectedOrigin = group
    }
}

export default ClientsStore
